import React, { useState, useEffect, useMemo, useRef, useCallback, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  getSyncJobCostCurrentSummary,
  getSyncJobCostSummary,
  getSyncJobCostPhases,
  getSyncJobCostsCurrent,
  getSyncJobCostMonthlySummary,
  getSyncJobCostArchivedSummary,
  saveJobCostRosterEntry,
  getProjects,
  getCostCodeNotes,
  addCostCodeNote,
} from '../api';
import LoadingState from '../components/LoadingState';
import StateScreen from '../components/ui/StateScreen';
import SegmentedControl from '../components/ui/SegmentedControl';
import {
  fmt$, fmtPct, spentColor, overTone, filterBucket, healthKey,
  HealthBadge, KpiCards, MonthlyChart, PhaseTable, BudgetBar, SyncChip, JcIcon,
  applyBudgetAssumption, buildPhases, buildMonthly, buildFinancials,
} from '../components/jobcost/JobCostShared';

/* ═══════════════════════════════════════════════════════════════════════════
   Job Costs dashboard (/job-costs) — warm-cream + sage redesign
   Portfolio-wide budget vs. cost across all ComputerEase jobs. Token-only
   colors. Visual reskin — CE-sync API, SSE live-sync, assumed-at-budget logic,
   and filter/sort all unchanged.
   ═══════════════════════════════════════════════════════════════════════════ */

const ratioOf = (j) => {
  const b = parseFloat(j.total_budget || 0);
  return b > 0 ? parseFloat(j.total_cost || 0) / b : null;
};
// Health-tier rank for the "Most Over Budget" sort so the badges group cleanly
// (over-budget → at-risk → on-track → under-budget → no-budget) instead of a pure
// dollar-overrun order intermixing tiers — a big under-budget job would otherwise
// outrank a small at-risk one, and no-budget jobs (budget 0 → positive overrun)
// would slip in among the over-budget ones. Uses the same healthKey as the badges.
const HEALTH_RANK = { over: 0, atbudget: 1, at: 2, on: 3, under: 4, none: 5 };
const healthRank = (j) => HEALTH_RANK[healthKey(ratioOf(j))] ?? 4;
const overrunOf = (j) => parseFloat(j.total_cost || 0) - parseFloat(j.total_budget || 0);

const normalizeNote = (n) => ({
  author: n.author || n.created_by || n.changed_by || n.user || '—',
  text: n.note || n.text || '',
  date: n.created_at || n.date || n.changed_at || null,
});

/* tokenized sort select */
const SortSelect = ({ value, onChange, options }) => (
  <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="jc-sort"
      style={{ appearance: 'none', WebkitAppearance: 'none', height: 32, padding: '0 32px 0 12px', borderRadius: 8, minWidth: 172, fontSize: 14, fontWeight: 500, color: 'var(--text-1)', fontFamily: 'var(--font-sans)', background: 'var(--bg-surface)', border: '1px solid var(--border-default)', boxShadow: 'var(--shadow-xs)', cursor: 'pointer', letterSpacing: '-0.005em' }}
    >
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
    <span style={{ position: 'absolute', right: 10, color: 'var(--text-3)', pointerEvents: 'none', display: 'inline-flex' }}><JcIcon name="chevron-d" size={14} /></span>
  </div>
);

/* portfolio KPI row — "Quiet Ledger": one calm surface panel, values neutral,
   color reserved for the figures that carry meaning (Committed = amber, matching
   the page-wide committed language; Net Over/Under = green under / red over).
   Collapses to a neutral card grid on narrow widths (see .pkr* CSS below). */
const KpiTile = ({ label, value, sub, valueColor, muted, valueSize = 26, variant }) => (
  <div className={'pkr-tile' + (variant ? ' pkr-tile-' + variant : '')} style={{ display: 'flex', flexDirection: 'column', gap: 9, flex: '1 1 180px', minWidth: 150, padding: '16px 20px' }}>
    <div style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: '0.075em', textTransform: 'uppercase', color: 'var(--text-3)' }}>{label}</div>
    <div className={'mono ' + (valueSize >= 30 ? 'pkr-hero-lg' : 'pkr-hero')} style={{ fontSize: valueSize, fontWeight: 800, lineHeight: 1.08, letterSpacing: '-0.03em', fontVariantNumeric: 'tabular-nums', minWidth: 0, overflowWrap: 'anywhere', color: muted ? 'var(--text-4)' : (valueColor || 'var(--text-1)') }}>{value}</div>
    <div className="mono" style={{ marginTop: 'auto', minHeight: 16, fontSize: 12, lineHeight: 1.35, color: 'var(--text-3)', fontVariantNumeric: 'tabular-nums' }}>{sub || ' '}</div>
  </div>
);
const KpiDivider = () => <div className="pkr-div" aria-hidden="true" style={{ flex: '0 0 auto', width: 1, alignSelf: 'stretch', margin: '14px 0', background: 'var(--border-subtle)' }} />;

/* Combined ledger tile — Budget / Spent / Committed folded into one calm tile
   (three labeled figures) so the row has room for the gross-margin tiles. Budget +
   Spent stay neutral (an aggregate spend ratio isn't per-job "risk", so it isn't
   ramped); Committed keeps the page-wide amber. */
const LedgerRow = ({ label, value, color, hint }) => (
  <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, minWidth: 0 }}>
    <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-3)', whiteSpace: 'nowrap', flex: '0 0 auto' }}>{label}</span>
    <span className="mono pkr-ledger" style={{ fontSize: 14.5, fontWeight: 700, color: color || 'var(--text-1)', fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em', textAlign: 'right', minWidth: 0, overflowWrap: 'anywhere' }}>
      {value}{hint ? <span style={{ color: 'var(--text-4)', fontWeight: 600, marginLeft: 6, whiteSpace: 'nowrap' }}>{hint}</span> : null}
    </span>
  </div>
);
const LedgerTile = ({ budget, cost, committed, spentPct, empty }) => (
  <div className="pkr-tile" style={{ display: 'flex', flexDirection: 'column', gap: 10, flex: '1.55 1 214px', minWidth: 172, padding: '16px 20px' }}>
    <div style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: '0.075em', textTransform: 'uppercase', color: 'var(--text-3)' }}>Budget · Spent · Committed</div>
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <LedgerRow label="Budget" value={empty ? '—' : fmt$(budget)} color={empty ? 'var(--text-4)' : undefined} />
      <LedgerRow label="Spent" value={empty ? '—' : fmt$(cost)} color={empty ? 'var(--text-4)' : undefined} hint={!empty && spentPct != null ? fmtPct(spentPct * 100) : null} />
      <LedgerRow label="Committed" value={committed ? fmt$(committed) : '—'} color={committed ? 'var(--warning)' : 'var(--text-4)'} />
    </div>
  </div>
);

/* Combined gross-margin tile — the % and $ (plus the contract they're a share of)
   folded into one calm ledger, parallel to Budget · Spent · Committed. Margin rows
   are sign-colored (green ≥0, red <0); contract is neutral. Blended/revenue-weighted
   (see totals memo); reads "–" when the filtered jobs carry no contract value. */
const GrossMarginTile = ({ gm, gmPct, contract, hasContract, color }) => (
  <div className="pkr-tile" style={{ display: 'flex', flexDirection: 'column', gap: 10, flex: '1.55 1 214px', minWidth: 172, padding: '16px 20px' }}>
    <div style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: '0.075em', textTransform: 'uppercase', color: 'var(--text-3)' }}>Gross Margin</div>
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <LedgerRow label="Margin %" value={hasContract && gmPct != null ? fmtPct(gmPct * 100) : '–'} color={hasContract ? color : 'var(--text-4)'} />
      <LedgerRow label="Margin $" value={hasContract ? fmt$(gm, { signed: true }) : '–'} color={hasContract ? color : 'var(--text-4)'} />
      <LedgerRow label="Contract" value={hasContract ? fmt$(contract) : '–'} color={hasContract ? undefined : 'var(--text-4)'} />
    </div>
  </div>
);

/* Net Over/Under sub — health counts; bold + tinted only when non-zero, zeros faint. */
const NetCount = ({ n, label, color }) => (
  <span style={{ color: n > 0 ? color : 'var(--text-4)', fontWeight: n > 0 ? 700 : 500 }}>{n} {label}</span>
);

const PortfolioKpiRow = ({ totals }) => {
  const t = totals || {};
  const empty = !(t.jobCount || t.budget || t.cost || t.committed);
  const spentPct = t.budget ? t.cost / t.budget : null;
  const over = t.netOverUnder > 0.5;    // dollars over budget = bad
  const under = t.netOverUnder < -0.5;  // dollars under budget = good
  const netColor = empty ? 'var(--text-4)' : over ? 'var(--danger)' : under ? 'var(--success)' : 'var(--text-1)';
  const netSub = empty ? null : (
    <>
      <NetCount n={t.overBudget || 0} label="over" color="var(--danger)" />
      <span style={{ color: 'var(--text-4)' }}> · </span>
      <NetCount n={t.atRisk || 0} label="at risk" color="var(--warning)" />
      <span style={{ color: 'var(--text-4)' }}> · </span>
      <NetCount n={t.onTrack || 0} label="on track" color="var(--success)" />
    </>
  );
  // Blended portfolio gross margin (see totals memo). Only meaningful when the
  // filtered jobs carry a contract value; otherwise the tiles read "–".
  const hasContract = (t.contract || 0) > 0;
  const gm = t.grossMargin || 0;
  const gmPct = t.grossMarginPct;
  const gmColor = !hasContract ? 'var(--text-4)' : gm >= 0 ? 'var(--success)' : 'var(--danger)';

  return (
    <section className="pkr" role="group" aria-label="Portfolio totals" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'stretch', gap: 0, padding: '4px 8px', background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 12, boxShadow: 'var(--shadow-sm)' }}>
      <KpiTile label="Active Jobs" value={(t.jobCount || 0).toLocaleString('en-US')} muted={!t.jobCount} valueSize={34} variant="count" />
      <KpiDivider />
      <LedgerTile budget={t.budget || 0} cost={t.cost || 0} committed={t.committed || 0} spentPct={spentPct} empty={empty} />
      <KpiDivider />
      <KpiTile label="Net Over / Under" value={fmt$(t.netOverUnder || 0, { signed: true })} muted={empty} valueColor={netColor} sub={netSub} variant="net" />
      <KpiDivider />
      <GrossMarginTile gm={gm} gmPct={gmPct} contract={t.contract || 0} hasContract={hasContract} color={gmColor} />
    </section>
  );
};

/* ─────────────── "there is more below" ───────────────
   Ported from Facility Navigator's useScrollEdges/ScrollHint pair (apps/web/src/pages/
   CustomersPage.tsx). Only half of FN's pattern transfers, and it is worth saying which:

     · FN's ScrollHint — the words — transfers directly.
     · FN's scrollEdges inset shadow does NOT. That trick darkens the scroll container's
       own bottom edge, which works because FN's scroller is a dialog whose box edge IS
       the edge you see. Measured on the deployed page, this page's scroller is the
       WINDOW: `.app-main` and `.main-content` both declare overflow-y:auto but neither
       has a constrained height, so both size to their content (7517px, room = 0) and
       document.scrollingElement does all the scrolling. An inset shadow on .app-main
       would render 7500px down the page, at the end of the content, not at the fold.
     · FN's other warning still binds, so there is no gradient either: a fade pinned over
       the last rows read as a washed-out blank band there, twice. Nothing is painted over
       a job row — the cue is a small pill in the viewport's own bottom margin.

   Why it is needed at all: macOS Chrome draws overlay scrollbars, so an unscrolled page
   gives no indication whatsoever that 63 rows continue past the fold.

   useLayoutEffect + ResizeObserver on <body>, not a plain useEffect: the list GROWS after
   mount (the CE sync fills in 63 jobs, then the archived section), so a single
   post-render measurement would decide "nothing to scroll", hide the cue, and only a real
   scroll would correct it — backwards, since the cue is for people who have not scrolled. */
const useMoreBelow = () => {
  const [more, setMore] = useState(false);
  useLayoutEffect(() => {
    const measure = () => {
      const el = document.scrollingElement || document.documentElement;
      const room = el.scrollHeight - el.clientHeight;
      // > 24 so a page that overflows by a hairline does not earn a cue
      setMore(room > 24 && el.scrollTop < room - 2);
    };
    measure();
    window.addEventListener('scroll', measure, { passive: true });
    window.addEventListener('resize', measure);
    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(measure);
      // Observe the element that actually GROWS, which is not the one that scrolls.
      // <body> and <html> are both viewport-sized here (862px at 1512x900) because the
      // shell's own wrappers are the tall ones — .app-main measures 7517px with 63 jobs
      // in it. Observing <body> meant the observer never fired at all: the cue stayed
      // hidden until a real scroll event, which is the exact backwards behaviour the
      // note above is about.
      const grows = document.querySelector('.app-main') || document.querySelector('.main-content') || document.body;
      ro.observe(grows);
    }
    return () => {
      window.removeEventListener('scroll', measure);
      window.removeEventListener('resize', measure);
      if (ro) ro.disconnect();
    };
  }, []);
  return more;
};

/* per-job collapsible card */
/* Column budget, and why these numbers. The previous set —
   'minmax(220px,1.5fr) minmax(190px,1.15fr) 112px 112px 112px 112px 86px 128px 26px'
   at gap 16 — had an intrinsic minimum of 1226px (410 of minmax floors + 688 of fixed
   tracks + 128 of gaps), while HEADER_MIN below claimed 1120. A card only gets
   `content width − 38` (its own border + 18px padding either side), and content width
   is `viewport − 300` with the sidebar out, so the row needed a 1564px window to lay
   out. Every narrower window — i.e. every laptop — silently cut the right-hand
   columns off, and macOS Chrome's overlay scrollbars drew nothing to say so.
   Two changes buy back the 118px that fixes it:
     · the "% Spent" column is gone. It rendered fmtPct(pct*100) in spentColor(pct) —
       the same expression, precision and colour as the BudgetBar caption's
       "101.0% spent" directly to its left. Byte-for-byte duplication, 86px + gap.
     · the money tracks go 112 → 124px. A full-cent 8-figure value ("$15,301,625.80")
       is 14 glyphs of 15px Geist Mono at -0.02em = 121.8px, so 112px tracks overflowed
       into the gutter at EVERY width — which is why two 14-digit numbers sat ~6px
       apart. They stay FIXED rather than auto/max-content: each card is its own grid,
       so content-sized tracks would resolve per-row and the columns would stop
       lining up down the list.
   Committed stays. Its column is the only cent-accurate source on the row — the
   caption rounds $10,650.16 to "$11k", and the in-bar label is suppressed entirely
   for a job like that (commW 0.07% « the 14% gate). */
const HEADER_COLS = 'minmax(200px,1.6fr) minmax(176px,1.25fr) 124px 124px 124px 124px 116px 22px';
const HEADER_GAP = 14;
// Keep in sync with HEADER_COLS: minmax floors (200+176) + fixed tracks (124*4+116+22)
// + 7 gaps. Unlike the old 1120 this is the grid's real min-content, so it is the
// width at which the row starts to scroll rather than a number that never binds.
const HEADER_MIN = 200 + 176 + 124 * 4 + 116 + 22 + 7 * HEADER_GAP;
const MicroLabel = ({ children }) => <div style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-3)', whiteSpace: 'nowrap' }}>{children}</div>;
const MetricCol = ({ label, value, color }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0, alignItems: 'flex-end' }}>
    <MicroLabel>{label}</MicroLabel>
    <span className="mono" style={{ fontSize: 15, fontWeight: 700, color: color || 'var(--text-1)', fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em', whiteSpace: 'nowrap' }}>{value}</span>
  </div>
);

const JobCard = ({ job, project, open, onToggle, onOpenProject, expandedData, getNotes, addNote, bare }) => {
  const budget = parseFloat(job.total_budget || 0);
  const cost = parseFloat(job.total_cost || 0);
  const committed = parseFloat(job.total_committed || 0);
  const overUnder = cost - budget;
  const pct = budget ? cost / budget : null;
  const name = project?.project_name || job.job_name || job.job_id;
  const number = project?.project_number || job.job_id;
  const customer = project?.client_name || '';

  return (
    <article style={{ background: bare ? 'transparent' : 'var(--bg-surface)', border: bare ? 'none' : '1px solid var(--border-default)', borderRadius: bare ? 0 : 12, boxShadow: bare ? 'none' : 'var(--shadow-sm)', overflow: 'hidden' }}>
      <div className="jc-jobhead" onClick={onToggle} role="button" aria-expanded={open} tabIndex={-1} style={{ cursor: 'pointer', padding: '15px 18px' }}>
        <div style={{ overflowX: 'auto' }}>
          <div style={{ display: 'grid', gap: HEADER_GAP, alignItems: 'center', minWidth: HEADER_MIN, gridTemplateColumns: HEADER_COLS }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5, minWidth: 0 }}>
              <span style={{ fontSize: 16.5, fontWeight: 700, color: 'var(--text-1)', letterSpacing: '-0.012em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                <button type="button" className="jc-projlink" onClick={(e) => { e.stopPropagation(); onOpenProject && onOpenProject(project?.slug || project?.id || job.job_id); }} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: 0, cursor: 'pointer', fontSize: 13, fontWeight: 500, color: 'var(--text-3)', background: 'none', border: 'none' }}>
                  <span className="mono" style={{ fontVariantNumeric: 'tabular-nums' }}>#{number}</span>
                  <span style={{ display: 'inline-flex', color: 'var(--text-4)' }}><JcIcon name="external" size={12} sw={1.7} /></span>
                </button>
                {customer && <><span style={{ color: 'var(--text-4)' }}>·</span><span style={{ fontSize: 13, color: 'var(--text-3)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>{customer}</span></>}
              </div>
            </div>
            <div onClick={(e) => e.stopPropagation()} style={{ cursor: 'default' }}>
              <BudgetBar budget={budget} cost={cost} committed={committed} />
            </div>
            <MetricCol label="Budget" value={fmt$(budget)} />
            <MetricCol label="Spent" value={fmt$(cost)} color={spentColor(pct)} />
            <MetricCol label="Committed" value={committed ? fmt$(committed) : '—'} color={committed ? 'var(--warning)' : 'var(--text-4)'} />
            <MetricCol label="Over / Under" value={fmt$(overUnder, { signed: true })} color={overTone(overUnder)} />
            {/* Right-aligned like the money columns beside it, so the badges end on a
                common edge next to the chevron instead of leaving a ragged gutter. */}
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}><HealthBadge pct={pct} size="sm" /></div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', color: 'var(--text-3)' }}>
              <span style={{ display: 'inline-flex', transition: 'transform 180ms ease', transform: open ? 'rotate(180deg)' : 'none' }}><JcIcon name="chevron-d" size={18} /></span>
            </div>
          </div>
        </div>
      </div>

      {open && (
        <div style={{ background: 'var(--bg-surface-2)', borderTop: '1px solid var(--border-subtle)', padding: '18px 18px 20px', display: 'flex', flexDirection: 'column', gap: 18 }}>
          {!expandedData ? (
            <div style={{ padding: '28px', display: 'flex', justifyContent: 'center' }}>
              <div style={{ width: 24, height: 24, borderRadius: 999, border: '2.5px solid var(--border-default)', borderTopColor: 'var(--accent)', animation: 'jcSpin 0.7s linear infinite' }} />
            </div>
          ) : (
            <>
              <KpiCards fin={buildFinancials(job, project)} />
              <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, margin: '0 2px 10px' }}>
                  <span style={{ fontSize: 13.5, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-3)' }}>Monthly Cost Trend</span>
                  <span className="mono" style={{ fontSize: 12.5, color: 'var(--text-4)', fontVariantNumeric: 'tabular-nums' }}>cost incurred per month · compact $</span>
                </div>
                <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 10, boxShadow: 'var(--shadow-xs)', padding: '14px 16px' }}>
                  <MonthlyChart monthly={expandedData.monthly} />
                </div>
              </div>
              <div>
                <span style={{ display: 'block', fontSize: 13.5, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-3)', margin: '0 2px 10px' }}>Phase Breakdown</span>
                <PhaseTable phases={expandedData.phases} jobId={job.job_id} getNotes={getNotes} addNote={addNote} />
              </div>
            </>
          )}
        </div>
      )}
    </article>
  );
};

/* ═════════ Job-cost lift-in-blur ═════════
   Clicking a job lifts its card toward the viewer, blurs the rest of the page, and
   unfolds the expanded detail (KPIs + monthly chart + phase table) inside the lifted
   card — the same focus interaction as the AR notes pop-out. */
const JCL_CSS = `
.jcl-layer{position:fixed;inset:0;z-index:1000}
.jcl-backdrop{position:absolute;inset:0;background:color-mix(in oklch, var(--bg-page) 52%, transparent);-webkit-backdrop-filter:blur(7px) saturate(.9);backdrop-filter:blur(7px) saturate(.9);opacity:0;transition:opacity .42s cubic-bezier(.3,.6,.3,1)}
.jcl-layer.is-open .jcl-backdrop{opacity:1}
.jcl-layer.is-closing .jcl-backdrop{opacity:0;transition-duration:.3s}
.jcl-card{position:fixed;display:flex;flex-direction:column;border-radius:14px;background:var(--bg-surface);border:1px solid transparent;overflow:hidden;transform:translate3d(0,0,0) scale(1);box-shadow:0 1px 2px oklch(0 0 0/0),0 12px 24px -8px oklch(0 0 0/0),0 32px 64px -14px oklch(0 0 0/0);transition:transform .55s cubic-bezier(.3,1.32,.32,1),box-shadow .55s cubic-bezier(.3,1,.35,1),border-color .3s ease;will-change:transform,box-shadow;outline:none}
.jcl-layer.is-open .jcl-card{transform:translate3d(0,var(--jcl-dy,0px),0) scale(1.012);border-color:var(--border-default);box-shadow:var(--shadow-lift)}
.jcl-layer.is-closing .jcl-card{transition:transform .36s cubic-bezier(.35,.9,.3,1),box-shadow .36s ease,border-color .25s ease}
.jcl-scroll{overflow-y:auto;overscroll-behavior:contain;scrollbar-width:thin;scrollbar-color:var(--border-strong) transparent}
.jcl-scroll::-webkit-scrollbar{width:9px}
.jcl-scroll::-webkit-scrollbar-thumb{background:var(--border-default);border-radius:9px;border:2px solid transparent;background-clip:content-box}
.jcl-x{position:absolute;top:13px;right:13px;z-index:4;width:32px;height:32px;border-radius:8px;display:inline-flex;align-items:center;justify-content:center;color:var(--text-3);background:var(--bg-surface);border:1px solid var(--border-default);box-shadow:var(--shadow-xs);cursor:pointer;opacity:0;transform:scale(.8);transition:opacity .3s ease .1s,transform .35s cubic-bezier(.3,1.4,.35,1) .1s,color .15s,border-color .15s,background .15s}
.jcl-layer.is-open .jcl-x{opacity:1;transform:scale(1)}
.jcl-x:hover{color:var(--text-1);border-color:var(--border-strong);background:var(--bg-surface-2)}
@media (prefers-reduced-motion: reduce){.jcl-backdrop,.jcl-card,.jcl-x{transition-duration:.01ms!important;transition-delay:0s!important}}
`;
let jclInjected = false;
const injectJclCss = () => {
  if (jclInjected || typeof document === 'undefined' || document.getElementById('jcl-css')) { jclInjected = true; return; }
  const s = document.createElement('style'); s.id = 'jcl-css'; s.textContent = JCL_CSS;
  document.head.appendChild(s); jclInjected = true;
};
const jclMotion = () => (typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) ? 0.05 : 1;

const JobCostLift = ({ job, project, rect, expandedData, getNotes, addNote, onOpenProject, returnFocus, onClosed }) => {
  injectJclCss();
  const [state, setState] = useState('pre'); // pre → open → closing
  const stRef = useRef(state); stRef.current = state;
  const [dy, setDy] = useState(0);
  const [vh, setVh] = useState(() => (typeof window !== 'undefined' ? window.innerHeight : 900));
  // Portaled to <body>, outside the app's `.app.dark` container — re-apply the theme.
  const [isDark] = useState(() => typeof document !== 'undefined' && !!document.querySelector('.app.dark, .theme-dark'));
  const cardRef = useRef(null);

  // Clamp the lifted card into the viewport, then spring open. Re-runs once when the
  // detail loads (card grows) and whenever the viewport HEIGHT changes — depending on
  // the stable `!!expandedData` flag (not the freshly-rebuilt object) avoids churning
  // on every parent render.
  useLayoutEffect(() => {
    const M = 20;
    const h = cardRef.current ? cardRef.current.offsetHeight : rect.height;
    const overflow = (rect.top + h + M) - vh;
    setDy(overflow > 0 ? -Math.round(Math.min(overflow, Math.max(rect.top - M, 0))) : 0);
    let r2; const r1 = requestAnimationFrame(() => { r2 = requestAnimationFrame(() => setState((s) => (s === 'pre' ? 'open' : s))); });
    const fb = setTimeout(() => setState((s) => (s === 'pre' ? 'open' : s)), 120);
    return () => { cancelAnimationFrame(r1); if (r2) cancelAnimationFrame(r2); clearTimeout(fb); };
  }, [rect.top, rect.height, expandedData ? 1 : 0, vh]);

  // Move focus into the dialog once on mount.
  useEffect(() => {
    const t = setTimeout(() => { if (cardRef.current) cardRef.current.focus({ preventScroll: true }); }, 40);
    return () => clearTimeout(t);
  }, []);

  const close = useCallback(() => {
    if (stRef.current === 'closing') return;
    setState('closing');
    setTimeout(() => {
      onClosed();
      if (returnFocus) requestAnimationFrame(() => requestAnimationFrame(() => { try { returnFocus.focus({ preventScroll: true }); } catch (e) { /* non-focusable */ } }));
    }, Math.max(40, 360 * jclMotion()));
  }, [onClosed, returnFocus]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      // Let an editable field (e.g. a phase note textarea) keep Escape so typing a
      // note and hitting Escape doesn't tear down the whole lift and lose the draft.
      const ae = document.activeElement;
      if (ae && (ae.tagName === 'TEXTAREA' || ae.tagName === 'INPUT' || ae.isContentEditable)) return;
      e.stopPropagation();
      close();
    };
    const w0 = window.innerWidth;
    const onResize = () => {
      // A width change means a real layout change → close. A height-only change
      // (mobile keyboard, window shorter) must NOT close, but must re-clamp so the
      // card's bottom stays reachable.
      if (window.innerWidth !== w0) close();
      else setVh(window.innerHeight);
    };
    window.addEventListener('keydown', onKey, true);
    window.addEventListener('resize', onResize);
    // Lock body scroll, compensating for the removed scrollbar so the blurred page
    // and the fixed card don't shift sideways on open/close.
    const prevOverflow = document.body.style.overflow;
    const prevPadR = document.body.style.paddingRight;
    const gutter = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = 'hidden';
    if (gutter > 0) document.body.style.paddingRight = gutter + 'px';
    return () => {
      window.removeEventListener('keydown', onKey, true);
      window.removeEventListener('resize', onResize);
      document.body.style.overflow = prevOverflow;
      document.body.style.paddingRight = prevPadR;
    };
  }, [close]);

  // Keep focus inside the card while open.
  useEffect(() => {
    const onFocusIn = (ev) => {
      if (stRef.current !== 'open') return;
      const c = cardRef.current;
      if (c && !c.contains(ev.target)) c.focus({ preventScroll: true });
    };
    document.addEventListener('focusin', onFocusIn);
    return () => document.removeEventListener('focusin', onFocusIn);
  }, []);

  const maxH = Math.max(320, vh - 40);

  return createPortal(
    <div className={'jcl-layer' + (isDark ? ' theme-dark' : '') + (state === 'open' ? ' is-open' : '') + (state === 'closing' ? ' is-closing' : '')} style={{ '--jcl-dy': dy + 'px' }}>
      <div className="jcl-backdrop" onClick={close} />
      <div
        className="jcl-card"
        ref={cardRef}
        role="dialog"
        aria-modal="true"
        tabIndex={-1}
        aria-label={'Job cost detail — ' + (project?.project_name || job.job_name || job.job_id)}
        style={{ left: rect.left, top: rect.top, width: rect.width, maxHeight: maxH }}
      >
        <button className="jcl-x" onClick={close} aria-label="Close job detail"><JcIcon name="x" size={16} sw={2} /></button>
        <div className="jcl-scroll">
          <JobCard job={job} project={project} open bare expandedData={expandedData} onToggle={close} onOpenProject={onOpenProject} getNotes={getNotes} addNote={addNote} />
        </div>
      </div>
    </div>,
    document.body
  );
};

/* ─────────────── archived jobs ─────────────── */

/* order the buckets from getSyncJobCostArchivedSummary: numeric years desc,
   then "Unfiled" last. Ignores empty buckets. */
const orderArchivedGroups = (archived) => {
  if (!archived || typeof archived !== 'object') return [];
  return Object.keys(archived)
    .filter((k) => Array.isArray(archived[k]) && archived[k].length > 0)
    .sort((a, b) => {
      if (a === 'Unfiled') return 1;
      if (b === 'Unfiled') return -1;
      return parseInt(b, 10) - parseInt(a, 10);
    })
    .map((k) => ({ key: k, rows: archived[k] }));
};

const ArchMetric = ({ label, value, color }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0, alignItems: 'flex-end' }}>
    <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-4)', whiteSpace: 'nowrap' }}>{label}</div>
    <span className="mono" style={{ fontSize: 13.5, fontWeight: 700, color: color || 'var(--text-2)', fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em', whiteSpace: 'nowrap' }}>{value}</span>
  </div>
);

const ARCH_ROW_COLS = 'minmax(150px,1.4fr) 104px 104px 104px 104px 78px 104px';

const ArchivedRow = ({ row, project, onRestore, restoring }) => {
  const budget = parseFloat(row.total_budget || 0);
  const cost = parseFloat(row.total_cost || 0);
  const committed = parseFloat(row.total_committed || 0);
  const overUnder = cost - budget;
  const pct = budget ? cost / budget : (row.percent_spent != null ? parseFloat(row.percent_spent) / 100 : null);
  const name = project?.project_name || row.job_name || row.job_id;
  const number = project?.project_number || row.job_id;

  return (
    <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 10, boxShadow: 'var(--shadow-xs)', padding: '12px 14px' }}>
      <div style={{ overflowX: 'auto' }}>
        <div style={{ display: 'grid', gap: 14, alignItems: 'center', minWidth: 900, gridTemplateColumns: ARCH_ROW_COLS }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
            <span style={{ fontSize: 14.5, fontWeight: 700, color: 'var(--text-1)', letterSpacing: '-0.01em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</span>
            <span className="mono" style={{ fontSize: 12.5, color: 'var(--text-4)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>#{number}</span>
          </div>
          <ArchMetric label="Budget" value={fmt$(budget)} />
          <ArchMetric label="Spent" value={fmt$(cost)} color={spentColor(pct)} />
          <ArchMetric label="Committed" value={committed ? fmt$(committed) : '—'} color={committed ? 'var(--warning)' : 'var(--text-4)'} />
          <ArchMetric label="Over / Under" value={fmt$(overUnder, { signed: true })} color={overTone(overUnder)} />
          <ArchMetric label="% Spent" value={pct == null ? '–' : fmtPct(pct * 100)} color={spentColor(pct)} />
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button type="button" className="jc-restore" onClick={() => !restoring && onRestore(row)} disabled={restoring} style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, height: 30, padding: '0 12px', borderRadius: 8, fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap',
              color: 'var(--accent-text)', background: 'var(--bg-surface)', border: '1px solid color-mix(in oklch, var(--accent) 30%, var(--border-default))',
              boxShadow: 'var(--shadow-xs)', cursor: restoring ? 'default' : 'pointer', opacity: restoring ? 0.6 : 1,
            }}>
              {restoring
                ? <span style={{ width: 12, height: 12, borderRadius: 999, border: '2px solid color-mix(in oklch, var(--accent) 30%, transparent)', borderTopColor: 'var(--accent)', animation: 'jcSpin 0.7s linear infinite', display: 'inline-block' }} />
                : <JcIcon name="refresh" size={12} sw={1.9} />}
              {restoring ? 'Restoring…' : 'Restore'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const ArchivedYearGroup = ({ yearKey, rows, projectLookup, onRestore, restoringId }) => {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ background: 'var(--bg-surface-2)', border: '1px solid var(--border-subtle)', borderRadius: 11, overflow: 'hidden' }}>
      <button type="button" onClick={() => setOpen((v) => !v)} className="jc-archyear" style={{
        display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left', padding: '11px 14px', cursor: 'pointer',
        background: 'transparent', border: 'none',
      }}>
        <span style={{ display: 'inline-flex', color: 'var(--text-3)', transition: 'transform 180ms ease', transform: open ? 'rotate(180deg)' : 'none' }}><JcIcon name="chevron-d" size={16} /></span>
        <span style={{ fontSize: 14.5, fontWeight: 700, color: 'var(--text-1)', letterSpacing: '-0.01em' }}>{yearKey}</span>
        <span className="mono" style={{ minWidth: 20, height: 20, padding: '0 7px', borderRadius: 999, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: 'var(--text-3)', background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)' }}>{rows.length}</span>
      </button>
      {open && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '0 12px 12px' }}>
          {rows.map((row) => (
            <ArchivedRow
              key={row.job_id}
              row={row}
              project={projectLookup[row.job_id]}
              onRestore={onRestore}
              restoring={restoringId === row.job_id}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const ArchivedSection = ({ groups, total, projectLookup, onRestore, restoringId }) => {
  const [open, setOpen] = useState(false);
  return (
    <section style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 12, boxShadow: 'var(--shadow-sm)', overflow: 'hidden' }}>
      <button type="button" onClick={() => setOpen((v) => !v)} className="jc-archhead" style={{
        display: 'flex', alignItems: 'center', gap: 11, width: '100%', textAlign: 'left', padding: '15px 18px', cursor: 'pointer',
        background: 'transparent', border: 'none',
      }}>
        <span style={{ width: 30, height: 30, borderRadius: 8, flex: '0 0 auto', color: 'var(--text-3)', background: 'var(--bg-surface-2)', border: '1px solid var(--border-subtle)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
          <JcIcon name="archive" size={16} sw={1.7} />
        </span>
        <span style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1, minWidth: 0 }}>
          <span style={{ fontSize: 15.5, fontWeight: 700, color: 'var(--text-1)', letterSpacing: '-0.012em' }}>Archived</span>
          <span style={{ fontSize: 13, color: 'var(--text-3)', lineHeight: 1.4 }}>Jobs filed under a year — data kept. Restore any to move it back to the active list.</span>
        </span>
        <span className="mono" style={{ minWidth: 22, height: 22, padding: '0 8px', borderRadius: 999, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 12.5, fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: 'var(--text-3)', background: 'var(--bg-surface-2)', border: '1px solid var(--border-subtle)', flex: '0 0 auto' }}>{total}</span>
        <span style={{ display: 'inline-flex', color: 'var(--text-3)', flex: '0 0 auto', transition: 'transform 180ms ease', transform: open ? 'rotate(180deg)' : 'none' }}><JcIcon name="chevron-d" size={18} /></span>
      </button>
      {open && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '2px 18px 18px', borderTop: '1px solid var(--border-subtle)' }}>
          <div style={{ height: 4 }} />
          {groups.map((g) => (
            <ArchivedYearGroup
              key={g.key}
              yearKey={g.key}
              rows={g.rows}
              projectLookup={projectLookup}
              onRestore={onRestore}
              restoringId={restoringId}
            />
          ))}
        </div>
      )}
    </section>
  );
};

/* ─────────────── page ─────────────── */

const SORT_OPTIONS = [
  { value: 'overrun', label: 'Most Over Budget' },
  { value: 'budget', label: 'Largest Budget' },
  { value: 'spent', label: '% Spent' },
  { value: 'name', label: 'Job Name' },
];

const JobCostDashboardPage = ({ onOpenProject }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [jobSummaries, setJobSummaries] = useState([]);
  const [projects, setProjects] = useState([]);
  const [lift, setLift] = useState(null); // { job, project, rect, returnFocus } — the lifted job pop-out
  const [phaseCache, setPhaseCache] = useState({});
  const [lineItemCache, setLineItemCache] = useState({});
  const [trendCache, setTrendCache] = useState({});
  const [notesCache, setNotesCache] = useState({});
  const [sortBy, setSortBy] = useState('overrun');
  const [filterHealth, setFilterHealth] = useState('all');
  const [filterPM, setFilterPM] = useState('');
  const [filterCM, setFilterCM] = useState('');
  const [syncToast, setSyncToast] = useState(null);
  const moreBelow = useMoreBelow();
  const [archived, setArchived] = useState({});
  const [restoringId, setRestoringId] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [currentData, monthlyData, projectList, archivedData] = await Promise.all([
        getSyncJobCostCurrentSummary({}).catch(() => []),
        getSyncJobCostSummary({}).catch(() => []),
        getProjects().catch(() => []),
        getSyncJobCostArchivedSummary().catch(() => ({})),
      ]);
      const summaries = (currentData && currentData.length > 0) ? currentData : (monthlyData || []);
      setJobSummaries(Array.isArray(summaries) ? summaries : []);
      setProjects(Array.isArray(projectList) ? projectList : projectList?.projects || []);
      setArchived(archivedData && typeof archivedData === 'object' ? archivedData : {});
    } catch (err) {
      setError(err.message || 'Failed to load job costs');
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshArchived = useCallback(async () => {
    try {
      const data = await getSyncJobCostArchivedSummary();
      setArchived(data && typeof data === 'object' ? data : {});
    } catch (err) {
      console.error('Archived summary refresh failed:', err);
    }
  }, []);

  const handleRestore = useCallback(async (row) => {
    if (restoringId) return;
    setRestoringId(row.job_id);
    try {
      await saveJobCostRosterEntry({ job_number: row.job_id, enabled: true });
      await Promise.all([loadData(), refreshArchived()]);
    } catch (err) {
      console.error('Restore failed:', err);
    } finally {
      setRestoringId(null);
    }
  }, [restoringId, loadData, refreshArchived]);

  useEffect(() => { loadData(); }, [loadData]);

  const loadDataRef = useRef(loadData);
  loadDataRef.current = loadData;
  const liftRef = useRef(null);
  liftRef.current = lift;
  useEffect(() => {
    const sseBase = process.env.NODE_ENV === 'production' ? '/api/sync/events' : 'http://localhost:5001/api/sync/events';
    const es = new EventSource(sseBase);
    es.addEventListener('sync-complete', (e) => {
      try {
        const data = JSON.parse(e.data);
        setPhaseCache({});
        setLineItemCache({});
        setTrendCache({});
        loadDataRef.current();
        // Repopulate an open lift's detail so it doesn't get stuck on a spinner.
        const openJid = liftRef.current && liftRef.current.job && liftRef.current.job.job_id;
        if (openJid && loadJobDetailRef.current) loadJobDetailRef.current(openJid, true);
        setSyncToast(data.job_name || data.job_id || 'Job costs');
        setTimeout(() => setSyncToast(null), 5000);
      } catch (err) {
        console.error('SSE parse error:', err);
      }
    });
    es.onerror = () => {};
    return () => es.close();
  }, []);

  const projectLookup = useMemo(() => {
    const map = {};
    projects.forEach((p) => {
      if (p.project_number) map[p.project_number] = p;
      if (p.ce_job_id) map[p.ce_job_id] = p;
    });
    return map;
  }, [projects]);

  // force=true bypasses the cache guards — used to repopulate an open lift after a
  // background sync clears the caches (the guards would otherwise see the stale,
  // still-populated closure and skip the refetch).
  const loadJobDetail = useCallback(async (jobId, force) => {
    if (force || !phaseCache[jobId]) {
      try { const phases = await getSyncJobCostPhases({ job_id: jobId, source: 'current' }); setPhaseCache((prev) => ({ ...prev, [jobId]: phases || [] })); } catch (err) { console.error('Phase load failed:', err); }
    }
    if (force || !lineItemCache[jobId]) {
      try { const raw = await getSyncJobCostsCurrent(jobId); setLineItemCache((prev) => ({ ...prev, [jobId]: applyBudgetAssumption(raw) })); } catch (err) { console.error('Line item load failed:', err); }
    }
    if (force || !trendCache[jobId]) {
      try { const trend = await getSyncJobCostMonthlySummary(jobId); setTrendCache((prev) => ({ ...prev, [jobId]: trend || [] })); } catch (err) { console.error('Trend load failed:', err); }
    }
    if (force || !notesCache[jobId]) {
      try { const notes = await getCostCodeNotes(jobId); setNotesCache((prev) => ({ ...prev, [jobId]: notes || [] })); } catch (err) { console.error('Notes load failed:', err); }
    }
  }, [phaseCache, lineItemCache, trendCache, notesCache]);
  const loadJobDetailRef = useRef(loadJobDetail);
  loadJobDetailRef.current = loadJobDetail;

  /* Click a job → measure its card and lift it into the blurred pop-out. Measure
     synchronously (React nulls e.currentTarget after the handler returns). */
  const openLift = useCallback((job, e) => {
    const head = e && e.currentTarget;
    const article = head && head.closest && head.closest('article');
    if (!article) return;
    const b = article.getBoundingClientRect();
    const proj = projectLookup[job.job_id];
    loadJobDetail(job.job_id);
    setLift((cur) => cur || { job, project: proj, rect: { left: b.left, top: b.top, width: b.width, height: b.height }, returnFocus: head });
  }, [loadJobDetail, projectLookup]);

  const getNotesFor = (jobId, phaseNo, code) =>
    (notesCache[jobId] || [])
      .filter((n) => String(n.phase) === String(phaseNo) && String(n.category || '') === String(code || ''))
      .map(normalizeNote);

  const addNoteFor = async (jobId, phaseNo, code, text) => {
    const created = await addCostCodeNote({ job_id: jobId, phase: phaseNo, category: code || '', note: text });
    setNotesCache((prev) => ({ ...prev, [jobId]: [...(prev[jobId] || []), created] }));
  };

  // People filters — match each job to its linked project's assigned people
  // (project_manager can hold several names; construction_manager one).
  const personFilteredJobs = useMemo(() => {
    if (!filterPM && !filterCM) return jobSummaries;
    return jobSummaries.filter((j) => {
      const proj = projectLookup[j.job_id];
      if (filterPM) {
        const pms = (proj?.project_manager || '').split(',').map((s) => s.trim());
        if (!pms.includes(filterPM)) return false;
      }
      if (filterCM) {
        const cms = (proj?.construction_manager || '').split(',').map((s) => s.trim());
        if (!cms.includes(filterCM)) return false;
      }
      return true;
    });
  }, [jobSummaries, filterPM, filterCM, projectLookup]);

  // Distinct assignable people, drawn from the projects behind jobs that have
  // cost data (so the dropdowns only list people actually on the board).
  const pmOptions = useMemo(() => {
    const set = new Set();
    jobSummaries.forEach((j) => (projectLookup[j.job_id]?.project_manager || '').split(',').map((s) => s.trim()).filter(Boolean).forEach((n) => set.add(n)));
    return [{ value: '', label: 'All PMs' }, ...[...set].sort().map((n) => ({ value: n, label: n }))];
  }, [jobSummaries, projectLookup]);
  const cmOptions = useMemo(() => {
    const set = new Set();
    jobSummaries.forEach((j) => (projectLookup[j.job_id]?.construction_manager || '').split(',').map((s) => s.trim()).filter(Boolean).forEach((n) => set.add(n)));
    return [{ value: '', label: 'All CMs' }, ...[...set].sort().map((n) => ({ value: n, label: n }))];
  }, [jobSummaries, projectLookup]);

  // The set the filter bar selects — status filter on top of the people filter;
  // drives BOTH the KPI tiles and the list, so the five portfolio tiles reflect
  // exactly what's filtered, not the whole book.
  const visibleJobs = useMemo(
    () => (filterHealth === 'all' ? personFilteredJobs : personFilteredJobs.filter((j) => filterBucket(ratioOf(j)) === filterHealth)),
    [personFilteredJobs, filterHealth]
  );

  const totals = useMemo(() => {
    const r = { budget: 0, cost: 0, committed: 0, contract: 0, jobCount: visibleJobs.length, overBudget: 0, atRisk: 0, onTrack: 0 };
    let gmCostBase = 0; // cost + open POs, summed ONLY over contract-bearing jobs
    visibleJobs.forEach((j) => {
      const jCost = parseFloat(j.total_cost || 0);
      const jCommitted = parseFloat(j.total_committed || 0);
      r.budget += parseFloat(j.total_budget || 0);
      r.cost += jCost;
      r.committed += jCommitted;
      const proj = projectLookup[j.job_id];
      const contract = parseFloat(proj?.revised_contract_value || proj?.contract_amount || 0);
      if (contract > 0) { r.contract += contract; gmCostBase += jCost + jCommitted; }
      const bucket = filterBucket(ratioOf(j));
      if (bucket === 'over') r.overBudget++;
      else if (bucket === 'at-risk') r.atRisk++;
      else r.onTrack++;
    });
    r.netOverUnder = r.cost - r.budget;
    // Blended portfolio gross margin: contract − (cost + open POs), matching the
    // per-job Gross Margin tiles (buildFinancials). Computed over jobs that HAVE a
    // contract (numerator + denominator use the same set) so a job with spend but
    // no contract entered can't silently deflate the margin. The % is revenue-
    // weighted (total margin ÷ total contract), NOT a mean of per-job percentages.
    r.grossMargin = r.contract - gmCostBase;
    r.grossMarginPct = r.contract > 0 ? r.grossMargin / r.contract : null;
    return r;
  }, [visibleJobs, projectLookup]);

  // Status-tab counts reflect the active people filter (but not the status itself),
  // so each tab shows how many of the selected people's jobs fall in that bucket.
  const counts = useMemo(() => ({
    all: personFilteredJobs.length,
    over: personFilteredJobs.filter((j) => filterBucket(ratioOf(j)) === 'over').length,
    'at-risk': personFilteredJobs.filter((j) => filterBucket(ratioOf(j)) === 'at-risk').length,
    'on-track': personFilteredJobs.filter((j) => filterBucket(ratioOf(j)) === 'on-track').length,
  }), [personFilteredJobs]);

  const filteredJobs = useMemo(() => {
    const list = [...visibleJobs];
    if (sortBy === 'overrun') list.sort((a, b) => (healthRank(a) - healthRank(b)) || (overrunOf(b) - overrunOf(a)));
    else if (sortBy === 'budget') list.sort((a, b) => parseFloat(b.total_budget || 0) - parseFloat(a.total_budget || 0));
    else if (sortBy === 'spent') list.sort((a, b) => (ratioOf(b) || 0) - (ratioOf(a) || 0));
    else if (sortBy === 'name') list.sort((a, b) => String(a.job_name || '').localeCompare(String(b.job_name || '')));
    return list;
  }, [visibleJobs, sortBy]);

  const archivedGroups = useMemo(() => orderArchivedGroups(archived), [archived]);
  const archivedTotal = useMemo(() => archivedGroups.reduce((sum, g) => sum + g.rows.length, 0), [archivedGroups]);

  if (loading) return <LoadingState message="Loading job costs…" />;

  // One button, two homes: the filter bar on the normal path, the standalone header on
  // the error/empty states (which render no filter bar). In the bar it is icon-only —
  // measured: the labelled button needs ~94px and the bar has ~100px of slack at 1512
  // only once the label is dropped. With it, a tenant whose PM names run long (the
  // selects size to their widest option — a 22-character name makes each ~198px) tipped
  // the bar onto a second line, spending 46px of the 48px the action row gave back.
  const refresh = (inBar) => (
    <button
      type="button"
      className="jc-refresh"
      onClick={loadData}
      title={inBar ? 'Refresh job costs' : undefined}
      aria-label={inBar ? 'Refresh job costs' : undefined}
      style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, height: 32, width: inBar ? 32 : undefined, padding: inBar ? 0 : '0 13px', borderRadius: 8, fontSize: 14, fontWeight: 500, color: 'var(--text-2)', background: 'var(--bg-surface)', border: '1px solid var(--border-default)', boxShadow: 'var(--shadow-xs)', cursor: 'pointer', flex: '0 0 auto' }}
    >
      <JcIcon name="refresh" size={13} />{inBar ? null : ' Refresh'}
    </button>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <style>{`
        @keyframes jcSpin { to { transform: rotate(360deg); } }
        /* Portfolio KPI row — "Quiet Ledger": one calm surface panel with hairline
           dividers; on narrow widths it dissolves into a tidy neutral card grid
           (same family as the expanded-job KpiCards) so wrapping never strands a divider. */
        /* Portfolio sums reach 9 figures (full-cent), which is what stopped the single
           hairline row of 4 tiles fitting below ~1600px. But the 2x2 fallback pays for
           a WIDTH problem in HEIGHT — 129 + 129 = 258px for the same eleven figures the
           one-row band shows in 129px — and it charges that to laptops, the screens
           with the least height to spare. So laptops keep the one-row band and we make
           the figures fit instead: the binding constraint is Net Over/Under, whose
           15-glyph full-cent value needs 222px at 26px and 183px at 21px, against the
           ~265-300px a quarter of the row gives at 1360-1599px. The 2x2 stays, but only
           below 1360px where 4-across genuinely cannot fit. >=1600px is untouched. */
        /* Floor measured, not guessed: at 1360 all four tiles still render every
           figure on one line (checked against the widest content each holds — the
           Net Over/Under sub-line and Gross Margin's 15-glyph Contract value — and
           re-checked with three-digit health counts, which also fit). Below 1360 the
           2x2 takes over. */
        @media (max-width: 1599px) and (min-width: 1360px) {
          .pkr { padding: 2px 6px !important; }
          .pkr .pkr-tile { padding: 12px 14px !important; min-width: 0 !important; }
          .pkr .pkr-div { margin: 10px 0 !important; }
          /* Width follows need, not flex-grow accident. Measured on the deployed page
             at 1512: the two ledger tiles took 335px each (grow 1.55) while Net
             Over/Under got 258px (grow 1) — 234px of inner width against the 237px
             its sub-line "5 over · 14 at risk · 44 on track" needs, so it wrapped to
             two lines. The ledger tiles' widest row ("Budget  $120,755,723.93") needs
             ~191px, so they had ~120px spare EACH. Pinning the two hero tiles and
             letting the ledgers absorb all variation gives 160/364/300/364: the sub
             clears with 39px to spare (enough for three-digit counts) at every width
             in the band, and Active Jobs stops reserving 258px for a 2-digit number. */
          .pkr .pkr-tile-count { flex: 0 1 160px !important; }
          .pkr .pkr-tile-net { flex: 0 1 300px !important; }
          .pkr .pkr-hero { font-size: 21px !important; }
          .pkr .pkr-hero-lg { font-size: 27px !important; }
          .pkr .pkr-ledger { font-size: 13.5px !important; }
        }
        /* The gap needs !important here: the section carries an inline "gap: 0" for
           the hairline-row layout, and inline styles outrank a plain declaration.
           Without it the four tiles in this grid render jammed edge-to-edge — which is
           what they did, despite this rule having asked for 10px all along. */
        @media (max-width: 1359px) {
          .pkr { display: grid !important; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px !important; padding: 0 !important; background: transparent !important; border: none !important; box-shadow: none !important; }
          .pkr .pkr-div { display: none; }
          .pkr .pkr-tile { min-width: 0 !important; background: var(--bg-surface); border: 1px solid var(--border-default); border-radius: 10px; box-shadow: var(--shadow-xs); }
        }
        @media (max-width: 640px) { .pkr { grid-template-columns: 1fr !important; } }
        /* Centred on the CONTENT column, not the viewport. A fixed element cannot
           inherit .main-content's margin-left, so the sidebar footprint is added back
           by hand: half of 252px expanded, half of 84px collapsed, and nothing below
           768px where App.css takes the sidebar off-canvas and zeroes the margin. */
        .jc-more { position: fixed; left: calc(50% + 126px); bottom: 18px; z-index: 890; transform: translateX(-50%);
                   display: inline-flex; align-items: center; gap: 6px; height: 26px; padding: 0 12px;
                   border-radius: 999px; font-family: var(--font-sans); font-size: 11.5px; font-weight: 600;
                   letter-spacing: 0.01em; color: var(--accent-text); background: var(--accent-soft);
                   border: 1px solid color-mix(in oklch, var(--accent) 22%, transparent);
                   box-shadow: var(--shadow-sm); pointer-events: none;
                   animation: jcMoreIn 220ms cubic-bezier(.3,.9,.35,1) both; }
        @keyframes jcMoreIn { from { opacity: 0; transform: translate(-50%, 6px); } to { opacity: 1; transform: translate(-50%, 0); } }
        .sidebar-collapsed .jc-more { left: calc(50% + 42px); }
        @media (max-width: 768px) { .jc-more { left: 50%; } }
        @media (prefers-reduced-motion: reduce) { .jc-more { animation-duration: .01ms; } }
        .jc-refresh:hover { border-color: color-mix(in oklch, var(--accent) 35%, var(--border-default)); color: var(--accent-text); background: var(--accent-soft); }
        .jc-jobhead:hover { background: color-mix(in oklch, var(--bg-hover) 45%, transparent); }
        .jc-projlink:hover, .jc-projlink:hover span { color: var(--accent-text) !important; }
        .j-note:hover { background: var(--accent-soft) !important; color: var(--accent-text) !important; }
        .j-note-ta:focus { border-color: var(--accent) !important; box-shadow: 0 0 0 3px var(--accent-ring) !important; }
        .jc-sort:focus-visible { outline: none; border-color: var(--accent); box-shadow: 0 0 0 3px var(--accent-ring); }
        .jc-archhead:hover, .jc-archyear:hover { background: color-mix(in oklch, var(--bg-hover) 45%, transparent); }
        .jc-restore:hover:not(:disabled) { border-color: color-mix(in oklch, var(--accent) 35%, var(--border-default)); background: var(--accent-soft); }
      `}</style>

      {/* Refresh + the sync chip live in the filter bar's right-hand cluster on the
          normal path. They used to sit in a dedicated action row, which spent 48px of
          vertical (32px header + 16px flex gap) on one 32px button and no words — the
          page title "Job Costs" is already in AppHeader. On a 14" screen that 48px is
          half a job row. The row survives only for the error and empty states, which
          have no filter bar to host the button. */}
      {(error || jobSummaries.length === 0) && (
        <header style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '0 2px' }}>
          <div style={{ flex: 1, minWidth: 0 }} />
          {refresh(false)}
        </header>
      )}

      {error ? (
        <StateScreen kind="server-error" title="Couldn’t load job costs" body="Something went wrong fetching the ComputerEase cost sync. Check your connection and try again." primary={{ label: 'Retry', onClick: loadData }} />
      ) : jobSummaries.length === 0 ? (
        <StateScreen kind="empty" title="No ComputerEase job costs found" body="Once jobs sync from ComputerEase, their budgets, costs, and phase breakdowns will appear here." />
      ) : (
        <>
          {/* gap 12 (was 16): the bar now also carries Refresh, and it must still lay
              out on one line at laptop widths or the 48px reclaimed above is spent
              again on a wrap. */}
          <section style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', padding: '10px 12px', background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 12, boxShadow: 'var(--shadow-sm)' }}>
            <SegmentedControl
              value={filterHealth}
              onChange={setFilterHealth}
              options={[
                { value: 'all', label: 'All', count: counts.all },
                { value: 'over', label: 'Over Budget', count: counts.over },
                { value: 'at-risk', label: 'At Risk', count: counts['at-risk'] },
                { value: 'on-track', label: 'On Track', count: counts['on-track'] },
              ]}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: '0 0 auto', flexWrap: 'wrap' }}>
              <SortSelect value={filterPM} onChange={setFilterPM} options={pmOptions} />
              <SortSelect value={filterCM} onChange={setFilterCM} options={cmOptions} />
              {(filterPM || filterCM) && (
                <button
                  type="button"
                  onClick={() => { setFilterPM(''); setFilterCM(''); }}
                  style={{ height: 32, padding: '0 11px', borderRadius: 8, fontSize: 13.5, fontWeight: 500, color: 'var(--text-3)', background: 'var(--bg-surface)', border: '1px solid var(--border-default)', boxShadow: 'var(--shadow-xs)', cursor: 'pointer' }}
                >
                  Clear
                </button>
              )}
            </div>
            <div style={{ flex: 1, minWidth: 8 }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 9, flex: '0 0 auto' }}>
              <span style={{ fontSize: 13.5, fontWeight: 500, color: 'var(--text-3)' }}>Sort by</span>
              <SortSelect value={sortBy} onChange={setSortBy} options={SORT_OPTIONS} />
              {refresh(true)}
            </div>
          </section>

          <PortfolioKpiRow totals={totals} />

          {filteredJobs.length === 0 ? (
            <StateScreen kind="empty" title="No jobs match this filter" body="Try a different health filter, or switch back to All to see every ComputerEase job." />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {filteredJobs.map((job) => {
                const project = projectLookup[job.job_id];
                const isLifted = lift && lift.job.job_id === job.job_id;
                return (
                  <div key={job.job_id} style={{ visibility: isLifted ? 'hidden' : undefined }}>
                    <JobCard
                      job={job}
                      project={project}
                      open={false}
                      onToggle={(e) => openLift(job, e)}
                      onOpenProject={onOpenProject}
                      expandedData={null}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {!error && archivedTotal > 0 && (
        <ArchivedSection
          groups={archivedGroups}
          total={archivedTotal}
          projectLookup={projectLookup}
          onRestore={handleRestore}
          restoringId={restoringId}
        />
      )}

      {/* Scroll cue. Only while the page actually has somewhere to go — it hides the
          moment you reach the bottom, and never renders when 63 jobs happen to fit. */}
      {moreBelow && !lift && (
        <div className="jc-more" aria-hidden="true">▾ more below</div>
      )}

      {/* Sync chip, floated out of the flow. It is a 5-second SSE notification
          (setSyncToast + a 5000ms clear) roughly 290px wide for a typical job name.
          It used to live in the action row, whose flex:1 spacer swallowed it; in the
          filter bar — which wraps — its arrival and departure would have shoved every
          job row down 46px and back twice a sync. Fixed, bottom-right, and below the
          lift's z-index so a focused card still covers it. */}
      {syncToast && (
        <div style={{ position: 'fixed', right: 24, bottom: 24, zIndex: 900, pointerEvents: 'none' }}>
          <SyncChip label={`${syncToast} — refreshed`} when="just now" />
        </div>
      )}

      {lift && (() => {
        const jid = lift.job.job_id;
        const ready = phaseCache[jid] && lineItemCache[jid] && trendCache[jid];
        const liftExpandedData = ready
          ? { phases: buildPhases(phaseCache[jid], lineItemCache[jid]), monthly: buildMonthly(trendCache[jid]) }
          : null;
        return (
          <JobCostLift
            job={lift.job}
            project={lift.project}
            rect={lift.rect}
            returnFocus={lift.returnFocus}
            expandedData={liftExpandedData}
            onOpenProject={onOpenProject}
            getNotes={(phaseNo, code) => getNotesFor(jid, phaseNo, code)}
            addNote={(phaseNo, code, text) => addNoteFor(jid, phaseNo, code, text)}
            onClosed={() => setLift(null)}
          />
        );
      })()}
    </div>
  );
};

export default JobCostDashboardPage;
